/* eslint-disable @typescript-eslint/member-ordering */
import { APP_BASE_HREF } from '@angular/common';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { SidepanelService } from 'mbs-ui-kit';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { Administrator, OAuthData } from 'mbs-ui-shared@models';
import { convertToClientUrl } from 'mbs-ui-shared@pipes';
import { EnvironmentService } from 'mbs-ui-shared@services';
import { AuthOnlineAccessService } from 'mbs-ui-shared@services/auth/auth-online-access.service';
import { BehaviorSubject, Observable, combineLatest, finalize, from, map, of, switchMap, tap } from 'rxjs';
import { take } from 'rxjs/operators';
import * as AuthStoreActions from '../store/auth/actions';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class LoginFacade {
  private loginLoading$ = new BehaviorSubject<boolean>(false);

  public get isAgentLocal(): boolean {
    return this.auth.isAgentLocal;
  }

  constructor(
    private auth: AuthFacade,
    private store: Store,
    private injector: Injector,
    private environment: EnvironmentService,
    private router: Router,
    private onlineAccessService: AuthOnlineAccessService,
    private sidepanelService: SidepanelService
  ) {}

  public loading$ = combineLatest([this.auth.loading$, this.loginLoading$.asObservable()]).pipe(
    map(([tokenLoading, loginLoading]) => tokenLoading || loginLoading)
  );

  // Login part

  public login({ email, password }): Observable<Administrator> {
    return (
      this.auth.isMBSMode || this.isAgentLocal
        ? this.auth.getAuthIdentity(
            new OAuthData({
              grant_type: 'password',
              username: email,
              password: password
            })
          )
        : of(null)
    ).pipe(
      switchMap(() => {
        if (this.isAgentLocal) return of(true);

        const isMBSMode = this.auth.isMBSMode;
        const MBSRequest = this.auth.loginRequest$({ email, password });
        const onlineAccessRequest = this.onlineAccessService.loginRequest$({ username: email, password }).pipe(
          map((res) => {
            this.auth.credentials = res;
            return true;
          })
        );
        const request = isMBSMode ? MBSRequest : onlineAccessRequest;

        this.loginLoading$.next(true);

        return request.pipe(finalize(() => this.loginLoading$.next(false)));
      }),
      switchMap(() => this.auth.fetchCurrentUser$)
    );
  }

  public logout(ref?: string): void {
    if (this.isAgentLocal) return void this.localAgentLogout();

    const redirectToLogin = () =>
      from(this.router.navigate(['login'])).pipe(
        tap(() => {
          this.auth.clearSessionData();
          this.store.dispatch(AuthStoreActions.logout());
          this.sidepanelService.closeAll();
        })
      );
    const getMBSRequest = () => {
      return this.environment.production
        ? this.prepareLogoutUrl(ref).pipe(
            map((url) => {
              this.auth.clearSessionData();
              location.replace(url);
              return true;
            })
          )
        : redirectToLogin();
    };
    const getOnlineAccessRequest = () => {
      return this.onlineAccessService.logoutRequest$(this.auth.refreshToken?.refresh_token ?? '').pipe(switchMap(() => redirectToLogin()));
    };

    (this.auth.isMBSMode ? getMBSRequest() : getOnlineAccessRequest()).subscribe();
  }

  private prepareLogoutUrl(ref?: string): Observable<string> {
    // otherwise cyclic dependency
    const router = this.injector.get(Router);
    const baseHref = this.injector.get(APP_BASE_HREF);
    const lastPage = router.url.replace('/AP/', '');
    // added ref on last page for return after authorization
    return of(convertToClientUrl(`/Admin/Login.aspx?logoff=1&ref=${ref || lastPage}`, baseHref));
  }

  private localAgentLogout(): void {
    this.auth
      .localAgentLogout$(this.auth.refreshToken?.refresh_token ?? '')
      .pipe(take(1))
      .subscribe({
        next: () => this.localAgentLogoutActions(),
        error: () => this.localAgentLogoutActions()
      });
  }

  private localAgentLogoutActions(): void {
    this.router.navigate(['/login']);
    this.auth.clearSessionData();
    this.store.dispatch(AuthStoreActions.logout());
    this.sidepanelService.removeAll();
  }
}
